import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./PublicFooter.css";

const COOKIE_NOTICE_KEY = "pcc_cookie_notice_accepted";

const ArrowUpIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="19" x2="12" y2="5"></line>
    <polyline points="5 12 12 5 19 12"></polyline>
  </svg>
);

const MailIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

/**
 * Cookie banner (public pages only), mounted via PublicFooter.
 * Note: Transactional pages typically do not render PublicFooter, so banner will not show there.
 */
const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    try {
      const v = window.localStorage.getItem(COOKIE_NOTICE_KEY);
      setIsVisible(v !== "1");
    } catch {
      // If localStorage is blocked, fail open (show banner).
      setIsVisible(true);
    }
  }, []);

  const accept = () => {
    try {
      window.localStorage.setItem(COOKIE_NOTICE_KEY, "1");
    } catch {
      // ignore
    }
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="ocf-cookieBanner" role="region" aria-label="Cookie notice">
      <div className="ocf-cookieBanner__inner">
        <div className="ocf-cookieBanner__text">
          <div className="ocf-cookieBanner__title">Cookies &amp; Privacy</div>
          <div className="ocf-cookieBanner__body">
            We use essential cookies to operate this website and keep it secure.
            <br />
            We do not use advertising cookies and we do not sell personal data.
          </div>
        </div>

        <div className="ocf-cookieBanner__actions">
          <Link to="/public/privacy" className="ocf-cookieBanner__link">
            Learn more
          </Link>
          <button
            type="button"
            className="ocf-cookieBanner__btn"
            onClick={accept}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

const PublicFooter: React.FC = () => {
  const year = new Date().getFullYear();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const routesLive: Record<string, boolean> = {
    "/public/find-family-doctor": true,
    "/public/how-it-works": true,
    "/public/intake-process": true,
    "/public/for-providers": true,
    "/public/about": true,
    "/public/accessibility": true,
    "/public/contact": true,
    "/public/privacy": true,
    "/public/terms": true,
    "/public/disclaimer": true,
  };

  const FooterLink = ({
    to,
    children,
  }: {
    to: string;
    children: React.ReactNode;
  }) => {
    if (routesLive[to]) {
      return (
        <Link to={to} className="ocf-footer-link">
          {children}
        </Link>
      );
    }
    return (
      <span
        className="ocf-footer-link ocf-footer-link--disabled"
        title="Coming soon"
      >
        {children}
      </span>
    );
  };

  return (
    <footer className="ocf-footer">
      {/* Cookie banner (bottom of page / viewport) */}
      <CookieBanner />

      <div className="ocf-footer-inner">
        {/* Brand Section */}
        <div className="ocf-footer-brand">
          <Link to="/public" className="ocf-footer-brandLink">
            <div className="ocf-footer-logoFrame" aria-hidden>
              <img src="/logo-1.png" alt="" />
            </div>
            <div className="ocf-footer-brandText">
              <div className="ocf-footer-name">AcceptingMD</div>
              <div className="ocf-footer-tagline">
                Connecting patients with family doctors
              </div>
            </div>
          </Link>

          {/* Contact email */}
          <a href="mailto:support@acceptingMD.ca" className="ocf-footer-email">
            <MailIcon />
            <span>support@acceptingMD.ca</span>
          </a>
        </div>

        {/* Navigation Columns */}
        <nav className="ocf-footer-columns" aria-label="Footer">
          <div className="ocf-footer-col">
            <div className="ocf-footer-colTitle">For Patients</div>
            <FooterLink to="/public/find-family-doctor">
              Find a Family Doctor
            </FooterLink>
            <FooterLink to="/public/how-it-works">How It Works</FooterLink>
            <FooterLink to="/public/intake-process">Intake Process</FooterLink>
          </div>

          <div className="ocf-footer-col">
            <div className="ocf-footer-colTitle">Resources</div>
            <FooterLink to="/public/about">About</FooterLink>
            <FooterLink to="/public/for-providers">For Providers</FooterLink>
            <FooterLink to="/public/contact">Contact Us</FooterLink>
          </div>

          <div className="ocf-footer-col">
            <div className="ocf-footer-colTitle">Legal</div>
            <FooterLink to="/public/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/public/terms">Terms of Use</FooterLink>
            <FooterLink to="/public/disclaimer">Disclaimer</FooterLink>
            <FooterLink to="/public/accessibility">Accessibility</FooterLink>
          </div>
        </nav>
      </div>

      {/* Bottom Bar */}
      <div className="ocf-footer-bottom">
        <div className="ocf-footer-bottom-inner">
          <div className="ocf-footer-copyright">
            © {year} AcceptingMD. All rights reserved.
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button
        type="button"
        className={`ocf-footer-backToTop ${
          showBackToTop ? "ocf-footer-backToTop--visible" : ""
        }`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ArrowUpIcon />
      </button>
    </footer>
  );
};

export default PublicFooter;
